import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { User, Mail, Building, Briefcase, Phone, Lock, Save, AlertCircle, CheckCircle, Bell } from 'lucide-react';

// Mirrors the category/severity vocabulary served by /api/notifications/preferences
const NOTIFICATION_CATEGORIES = [
  { value: 'compliance_risk', label: 'Compliance Risk' },
  { value: 'regulatory_change', label: 'Regulatory Changes' },
  { value: 'deadline_approaching', label: 'Deadlines' },
  { value: 'deadline', label: 'Deadline Alerts' },
  { value: 'overdue_task', label: 'Overdue Tasks' },
  { value: 'data_quality', label: 'Data Quality' },
  { value: 'anomaly_detected', label: 'Anomalies' },
  { value: 'recommendation', label: 'Recommendations' },
  { value: 'agent_insight', label: 'Agent Insights' },
  { value: 'system_alert', label: 'System Alerts' }
];

const SEVERITY_OPTIONS = [
  { value: 'info', label: 'All severities (info and up)' },
  { value: 'low', label: 'Low and above' },
  { value: 'medium', label: 'Medium and above' },
  { value: 'high', label: 'High and above' },
  { value: 'critical', label: 'Critical only' }
];

const DIGEST_OPTIONS = [
  { value: 'none', label: 'No digest' },
  { value: 'daily', label: 'Daily digest' },
  { value: 'weekly', label: 'Weekly digest' }
];

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [prefsSaving, setPrefsSaving] = useState(false);

  useEffect(() => {
    fetchUserProfile();
    fetchNotificationPrefs();
  }, []);

  const fetchNotificationPrefs = async () => {
    const data = await apiFetch(`${API}/notifications/preferences`);
    if (data?._error) return; // section stays hidden if the API is unavailable
    setPrefs({
      muted_categories: data.muted_categories || [],
      min_severity: data.min_severity || 'info',
      digest: data.digest || 'none'
    });
  };

  const toggleMutedCategory = (category) => {
    setPrefs(prev => ({
      ...prev,
      muted_categories: prev.muted_categories.includes(category)
        ? prev.muted_categories.filter(c => c !== category)
        : [...prev.muted_categories, category]
    }));
  };

  const handleSavePreferences = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setPrefsSaving(true);
    const data = await apiFetch(`${API}/notifications/preferences`, { method: 'PUT', body: prefs });
    setPrefsSaving(false);
    if (data?._error) {
      setError(data.message || 'Failed to save notification preferences');
      return;
    }
    setSuccess('Notification preferences saved!');
  };

  const fetchUserProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }
    const data = await apiFetch(`${API}/auth/me`);
    if (data?._error) {
      // Try to load from localStorage as fallback
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          setFormData(userData);
          setLoading(false);
          return;
        } catch (parseErr) {
          // Continue to error handling
        }
      }
      setError('Failed to load profile');
      setLoading(false);
      return;
    }
    setUser(data);
    setFormData(data);
    setLoading(false);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const updateData = {
      full_name: formData.full_name,
      organization: formData.organization,
      job_title: formData.job_title,
      department: formData.department,
      phone: formData.phone
    };

    const data = await apiFetch(`${API}/auth/me`, { method: 'PUT', body: updateData });
    if (data?._error) {
      setError(data.message || 'Failed to update profile');
      return;
    }

    setSuccess('Profile updated successfully!');
    setEditing(false);
    fetchUserProfile();
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    const data = await apiFetch(`${API}/auth/change-password`, {
      method: 'POST',
      body: {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }
    });
    if (data?._error) {
      setError(data.message || 'Failed to change password');
      return;
    }

    setSuccess('Password changed successfully!');
    setShowPasswordForm(false);
    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-lg p-8 text-white">
          <div className="flex items-center gap-3">
            <User size={36} />
            <div>
              <h1 className="text-3xl font-bold">Profile & Team</h1>
              <p className="text-emerald-100">Manage your account settings</p>
            </div>
          </div>
        </div>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
          <p className="text-sm text-red-700">{error || 'Unable to load profile. Please try logging in again.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-lg p-8 text-white">
        <div className="flex items-center gap-3">
          <User size={36} />
          <div>
            <h1 className="text-3xl font-bold">My Profile</h1>
            <p className="text-emerald-100">Manage your account settings</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
          <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Profile Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">Profile Information</h2>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Edit Profile
            </button>
          )}
        </div>

        {editing ? (
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email (Read-only)
                </label>
                <input
                  type="email"
                  value={formData.email}
                  disabled
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg bg-slate-100 text-slate-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Organization
                </label>
                <input
                  type="text"
                  value={formData.organization || ''}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Job Title
                </label>
                <input
                  type="text"
                  value={formData.job_title || ''}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Department
                </label>
                <input
                  type="text"
                  value={formData.department || ''}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
              >
                <Save size={18} />
                Save Changes
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setFormData(user);
                }}
                className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-3">
                <User className="text-slate-400" size={20} />
                <div>
                  <p className="text-xs text-slate-500">Full Name</p>
                  <p className="text-slate-900 font-medium">{user.full_name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="text-slate-400" size={20} />
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="text-slate-900 font-medium">{user.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Building className="text-slate-400" size={20} />
                <div>
                  <p className="text-xs text-slate-500">Organization</p>
                  <p className="text-slate-900 font-medium">{user.organization || 'Not set'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Briefcase className="text-slate-400" size={20} />
                <div>
                  <p className="text-xs text-slate-500">Job Title</p>
                  <p className="text-slate-900 font-medium">{user.job_title || 'Not set'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <User className="text-slate-400" size={20} />
                <div>
                  <p className="text-xs text-slate-500">Department</p>
                  <p className="text-slate-900 font-medium">{user.department || 'Not set'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="text-slate-400" size={20} />
                <div>
                  <p className="text-xs text-slate-500">Phone</p>
                  <p className="text-slate-900 font-medium">{user.phone || 'Not set'}</p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Role</p>
                  <p className="font-medium text-slate-900 capitalize">{user.role}</p>
                </div>
                <div>
                  <p className="text-slate-500">Account Created</p>
                  <p className="font-medium text-slate-900">
                    {new Date(user.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Last Login</p>
                  <p className="font-medium text-slate-900">
                    {user.last_login ? new Date(user.last_login).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notification Preferences */}
      {prefs && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-6">
            <Bell className="text-slate-600" size={24} />
            <div>
              <h2 className="text-xl font-bold text-slate-900">Notification Preferences</h2>
              <p className="text-sm text-slate-600">Choose which notifications reach you and how often</p>
            </div>
          </div>

          <form onSubmit={handleSavePreferences} className="space-y-6">
            <div>
              <p className="block text-sm font-medium text-slate-700 mb-2">
                Categories <span className="font-normal text-slate-500">(unchecked categories are muted)</span>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {NOTIFICATION_CATEGORIES.map(({ value, label }) => (
                  <label
                    key={value}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">{label}</p>
                      <p className="text-xs text-slate-500 font-mono">{value}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={!prefs.muted_categories.includes(value)}
                      onChange={() => toggleMutedCategory(value)}
                      className="h-4 w-4 accent-emerald-600"
                    />
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Minimum Severity
                </label>
                <select
                  value={prefs.min_severity}
                  onChange={(e) => setPrefs(prev => ({ ...prev, min_severity: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {SEVERITY_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Digest
                </label>
                <select
                  value={prefs.digest}
                  onChange={(e) => setPrefs(prev => ({ ...prev, digest: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {DIGEST_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={prefsSaving}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <Save size={18} />
              {prefsSaving ? 'Saving...' : 'Save Preferences'}
            </button>
          </form>
        </div>
      )}

      {/* Password Management */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Lock className="text-slate-600" size={24} />
            <div>
              <h2 className="text-xl font-bold text-slate-900">Password</h2>
              <p className="text-sm text-slate-600">Change your password</p>
            </div>
          </div>
          {!showPasswordForm && (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
            >
              Change Password
            </button>
          )}
        </div>

        {showPasswordForm && (
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Current Password
              </label>
              <input
                type="password"
                required
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  required
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-xs text-slate-500 mt-1">Min. 8 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  required
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                Update Password
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                }}
                className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Profile;